import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useMatches,
  useRouteLoaderData,
} from '@remix-run/react'
import type {HeadersFunction, LinksFunction, LoaderFunction, TypedResponse} from '@remix-run/node'
import {json} from '@remix-run/node'
import {useCallback, useEffect, useState} from 'react'
import {useCustomStyle} from '~/hooks/use-custom-style'
import styles from './styles/tailwind.css?url'
import {
  appendCookieIfDefined,
  gdprConsentCookie,
  pageIdCookie,
  sessionIdCookie,
  sharingPolicyCookie,
  userIdCookie,
} from './utils/cookies.server'
import {pageLoadTrackingNeeded, trackPageLoad} from './utils/tracking.server'
import type {DeviceTypes, ProductStoreDataLoaderResponse, RootLoaderResponse} from './types'
import Footer from './components/footer/footer'
import {generateUniqueId} from './utils/calculate-object-hash'
import {GoogleAnalytics} from './utils/ga/ga-script-config'
import {GoogleAnalyticsGdpr} from './utils/ga/ga-script-gdpr-config'
import AppBarAboveTheList from './components/appbar/AppbarAboveTheList'

import {FcbPixelScript} from './utils/fcb-pixel/fcb-pixel-script'
import {BingPixelScript} from './utils/bing-pixel/bing-pixel-script'
import {HotjarScript} from './utils/hotjar/hotjar-script'
import {setEnvironmentWith} from './utils/async-storage.server'
import ScrollToHash from './components/commons/scroll-to-hash'
import type {DomainConfig} from './domains'
import {getDomainConfig} from './domains'
import {isPrefixedListRoute} from './utils/list-variation'
import type {DOMAIN_CONFIG_BY_HOSTNAME} from './domains/hostname-to-config'
import {cloudfrontBaseUrl} from './utils/constants'
import CookieConsent from './components/cookie-consent'
import {ChristmasEventLogo} from './icons/sale-events/christmas-logo'

const heroImageByDomainName: Record<
  Extract<
    DomainConfig['domainName'],
    'bestpatrioticpicks' | 'eaglereviews' | 'topstatesreviews' | 'americanbest10' | 'canadatoppicks' | 'canadabest10'
  >,
  Record<Extract<DeviceTypes, 'desktop' | 'smartphone'>, string>
> = {
  bestpatrioticpicks: {
    desktop: `${cloudfrontBaseUrl}/maga/hero_images/patriotic_hero_2_d.webp`,
    smartphone: `${cloudfrontBaseUrl}/maga/hero_images/patriotic_hero_m.webp`,
  },
  eaglereviews: {
    desktop: `${cloudfrontBaseUrl}/maga/hero_images/patriotic_hero_2_d.webp`,
    smartphone: `${cloudfrontBaseUrl}/maga/hero_images/patriotic_hero_m.webp`,
  },
  topstatesreviews: {
    desktop: `${cloudfrontBaseUrl}/maga/hero_images/top_states_hero_d.webp`,
    smartphone: `${cloudfrontBaseUrl}/maga/hero_images/top_states_hero_m.webp`,
  },
  americanbest10: {
    desktop: `${cloudfrontBaseUrl}/maga/hero_images/american_best_hero_d.webp`,
    smartphone: `${cloudfrontBaseUrl}/maga/hero_images/american_best_hero_m.webp`,
  },
  canadatoppicks: {
    desktop: `${cloudfrontBaseUrl}/maga/hero_images/canada_picks_top_d.webp`,
    smartphone: `${cloudfrontBaseUrl}/maga/hero_images/canada_top_picks_m.webp`,
  },
  canadabest10: {
    desktop: `${cloudfrontBaseUrl}/maga/hero_images/canada_best_10_d.webp`,
    smartphone: `${cloudfrontBaseUrl}/maga/hero_images/canada_best_10_m.webp`,
  },
}

function setReliableSiteBadgeAltText() {
  if (typeof document === 'undefined') return

  const badgeImages = document.querySelectorAll<HTMLImageElement>('img[src*="reliablesite.org/badges"]')
  badgeImages.forEach((img) => {
    if (!img.getAttribute('alt')) {
      img.setAttribute('alt', 'ReliableSite badge')
    }
  })
}

export const links: LinksFunction = () => [
  {
    rel: 'preconnect',
    href: cloudfrontBaseUrl,
    crossOrigin: 'anonymous',
  },
  {rel: 'preconnect', href: 'https://images-proxy.bestreviews.guide', crossOrigin: 'anonymous'},
  {rel: 'preload', href: styles, as: 'style'},
  {rel: 'stylesheet', href: styles},
]

export function ErrorBoundary() {
  return (
    <html lang="en">
      <head>
        <title>Oops!</title>
        <Links />
      </head>
      <body>
        <main>
          <div className="flex h-full w-full justify-center bg-[url('https://res.cloudinary.com/roundforest/image/upload/c_pad,b_auto:predominant,fl_preserve_transparency/v1697807351/BRG/error.jpg')] bg-cover bg-center bg-no-repeat">
            <div className="content-container mx-auto mt-56 flex h-screen flex-col justify-start gap-8 text-center">
              <h1 className="text-7xl font-bold tracking-wide text-indigo-400 tablet:text-6xl mobile:text-5xl">
                Something went wrong
              </h1>
              <h3 className="text-2xl font-medium tracking-wide tablet:text-xl">
                Looks like something went wrong. Were working on it.
              </h3>
            </div>
          </div>
        </main>
        <Scripts />
      </body>
    </html>
  )
}

export const headers: HeadersFunction = ({loaderHeaders}) => {
  const headers = new Headers()
  if (loaderHeaders.get('Cache-Control')) {
    headers.append('Cache-Control', loaderHeaders.get('Cache-Control') ?? 'no-cache')
  }
  return headers
}

export const loader: LoaderFunction = async ({request, context}): Promise<TypedResponse<RootLoaderResponse>> => {
  setEnvironmentWith(context)
  const {
    locale,
    language,
    currencyCode,
    forcedLanguage,
    featureFlags,
    deviceType,
    referrerUrl,
    cookies,
    userAgent,
    hostname,
    userId,
    sessionId,
    pageLoadId,
    routeName,
    domain,
    isBot,
    skipFollowLinks,
    traffic,
    variation,
  } = context
  const cookieHeader = request.headers.get('Cookie')
  const sharingPolicy = await sharingPolicyCookie.parse(cookieHeader)

  const url = new URL(request.url)
  const {searchParams} = url
  const forcedDomain = searchParams?.get('forcedDomain') as keyof typeof DOMAIN_CONFIG_BY_HOSTNAME
  const forcedLang = searchParams?.get('forcedLang') as string
  const forcedLocale = searchParams?.get('forcedLocale') as string

  const utmSource = searchParams?.get('utm_source')
  const shouldLoadFacebookPixel = utmSource === 'facebook'

  const {domainName, domainType, measurementId, favicon} = getDomainConfig(
    forcedDomain ?? domain,
    forcedLocale ?? locale,
  )

  const usePageLoadTracking = pageLoadTrackingNeeded(request.url)
  const id = generateUniqueId()
  const renderTimestamp = Date.now()

  const headers = new Headers()

  const gdprConsentData = (await gdprConsentCookie.parse(cookieHeader)) || null
  const isGdprAccepted = gdprConsentData && gdprConsentData === 'granted'

  if (featureFlags?.cookieConsentVariation === 'opt-in') {
    headers.set('Cache-Control', 'no-cache')
    if (isGdprAccepted) {
      await appendCookieIfDefined(headers, userIdCookie, userId)
      await appendCookieIfDefined(headers, sessionIdCookie, sessionId)
      await appendCookieIfDefined(headers, pageIdCookie, pageLoadId)
    }
  } else {
    await appendCookieIfDefined(headers, userIdCookie, userId)
    await appendCookieIfDefined(headers, sessionIdCookie, sessionId)
    await appendCookieIfDefined(headers, pageIdCookie, pageLoadId)
  }

  let responseHeaders = headers
  let pageLoadData
  const pathSlug = !['internal_route', 'healthz', 'file', 'redirect', 'report_redirect'].includes(routeName)
    ? request.url.match(/^(?:https?:\/\/)?(?:[^/]+)(?:\/)([^?#]*)?/)?.[1]
    : undefined
  // The LEVANTA page is the same list under /lv/ (or /lv2/ or /cc/) and the VERDICT page under /pick/ (or /pick2/): report the
  // catalog slug and let `variation` carry the difference.
  const slug = isPrefixedListRoute(routeName) ? pathSlug?.replace(/^(lv2?|cc|pick2?)\//, '') : pathSlug

  if (featureFlags?.cookieConsentVariation === 'opt-in') {
    const trackingDataGdpr = await trackPageLoad({
      locale,
      cookies,
      userAgent,
      slug,
      referrerUrl,
      hostname,
      featureFlags,
      variation,
      url: request.url,
      headers: responseHeaders,
    })
    if (usePageLoadTracking && isGdprAccepted) {
      responseHeaders = trackingDataGdpr.responseHeaders
    }
    pageLoadData = trackingDataGdpr.trackingData
  } else {
    if (usePageLoadTracking) {
      const trackingData = await trackPageLoad({
        locale,
        cookies,
        userAgent,
        slug,
        referrerUrl,
        hostname,
        featureFlags,
        variation,
        url: request.url,
        headers: responseHeaders,
      })
      responseHeaders = trackingData.responseHeaders
    }
  }

  const trackAnalytics = {
    permission: gdprConsentData,
    user_id: userId,
    session_id: sessionId,
    page_id: pageLoadId,
    pageLoadData,
  }

  const shouldLoadHeroImage = routeName === 'slug_page' && domainType === 'maga'

  return json<RootLoaderResponse>(
    {
      locale: forcedLocale ?? locale,
      language: forcedLang ?? language,
      currencyCode,
      forcedLanguage,
      isUSLocale: locale.toLowerCase() === 'us',
      id,
      isBot,
      routeName,
      heroImage:
        shouldLoadHeroImage && domainName && domainName in heroImageByDomainName
          ? heroImageByDomainName[domainName as keyof typeof heroImageByDomainName]
          : undefined,
      pageLoadIdCookie: pageLoadId,
      googleAnalyticsMeasurementId: measurementId,
      cookieConsentVariation: featureFlags?.cookieConsentVariation ?? 'got-it',
      featureFlags,
      gdprConsentData,
      hostname,
      skipFollowLinks,
      referrerUrl,
      favicon,
      traffic,
      domain: forcedDomain ?? domain,
      domainName,
      deviceType,
      googleAdsKey: process.env.GOOGLE_ADS_KEY,
      googleAdsId: process.env.GOOGLE_ADS_ID,
      googleAdsRemarketingKey: process.env.GOOGLE_ADS_REMARKETING_KEY,
      fcbPixelId: process.env.FCB_PIXEL_ID,
      trackAnalytics,
      serverSideVariables: context,
      initialClickId: String(Date.now()),
      renderTimestamp,
      shouldLoadFacebookPixel,
      sharingPolicy: {requested: sharingPolicy?.sharingPolicyRequest},
    },
    {headers: responseHeaders},
  )
}

export default function App() {
  const {
    pageLoadIdCookie,
    featureFlags,
    id,
    isBot,
    googleAnalyticsMeasurementId,
    googleAdsKey,
    trackAnalytics,
    fcbPixelId,
    deviceType,
    favicon,
    initialClickId,
    skipFollowLinks,
    heroImage,
    shouldLoadFacebookPixel,
    sharingPolicy,
    cookieConsentVariation,
    routeName,
  } = useLoaderData() as RootLoaderResponse
  useCustomStyle()

  const [showCookieConsentGdpr, setShowCookieConsentGdpr] = useState(!trackAnalytics?.permission)

  const location = useMatches()
  const isHomePage = location.some(({id}) => id === 'routes/_index')
  const isCategoriesPage = location.some(({id}) => id.includes('routes/categories'))
  const isProductPage = location.some(({id}) => id.includes('routes/pp'))

  const productStorePageRouteData = useRouteLoaderData('routes/pp.$slug.$productSlug') as ProductStoreDataLoaderResponse

  const enableHotjar = featureFlags?.enableHotjarScript && !isBot && process.env.NODE_ENV === 'production'

  // The /pick/ page keeps the notice under the header on every device (its sticky bar owns the bottom edge).
  const isPickPage = routeName === 'pick_slug_page'
  const showCookieConsentOnBottom = (isHomePage || isCategoriesPage || deviceType !== 'desktop') && !isPickPage

  const [clickId, setClickId] = useState(initialClickId)

  const updateClickId = useCallback(() => {
    setClickId(String(Date.now()))
  }, [])

  const sharingPolicyDenied = featureFlags?.showCcpaAndSharingPolicy && sharingPolicy?.requested
  const optInOutConsent = cookieConsentVariation === 'opt-in' || cookieConsentVariation === 'opt-out'

  useEffect(() => {
    const handleSharingPolicySubmit = () => {
      if (typeof window !== 'undefined') {
        if (window.gtag) {
          window.gtag = function () {}
          window.dataLayer = []
        }
        if (window.fbq) {
          window.fbq('consent', 'revoke', {})
        }
      }
    }
    window.addEventListener('sharing-policy-submitted', handleSharingPolicySubmit)
    return () => window.removeEventListener('sharing-policy-submitted', handleSharingPolicySubmit)
  }, [])

  useEffect(() => {
    setReliableSiteBadgeAltText()

    const observer = new MutationObserver(() => {
      setReliableSiteBadgeAltText()
    })

    observer.observe(document.body, {childList: true, subtree: true})
    return () => observer.disconnect()
  }, [])

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        {skipFollowLinks ? (
          <meta name="robots" content="noindex, nofollow" />
        ) : (
          <meta name="robots" content="follow, index" />
        )}
        <Meta />
        <link rel="icon" href={favicon} type="image/png" sizes="32x32" />

        {isProductPage && productStorePageRouteData?.storeData?.productImages?.[0]?.large && (
          <link
            rel="preload"
            fetchpriority="high"
            as="image"
            href={productStorePageRouteData?.storeData?.productImages?.[0]?.large}
            media="(min-width: 1024px)"
          />
        )}
        {heroImage?.desktop && (
          <link rel="preload" fetchpriority="high" as="image" href={heroImage?.desktop} media="(min-width: 1024px)" />
        )}
        {heroImage?.smartphone && (
          <link
            rel="preload"
            as="image"
            href={heroImage?.smartphone}
            fetchpriority="high"
            media="(max-width: 1023px)"
          />
        )}
        <Links />

        {featureFlags?.enableBingPixelScript && !sharingPolicyDenied ? <BingPixelScript /> : null}
        {shouldLoadFacebookPixel && !sharingPolicyDenied ? <FcbPixelScript fcbPixelId={fcbPixelId} /> : null}
        {enableHotjar ? <HotjarScript /> : null}
      </head>
      <body>
        {optInOutConsent ? (
          <GoogleAnalyticsGdpr
            googleAdsKey={googleAdsKey}
            pageLoadIdCookie={pageLoadIdCookie}
            measurementId={googleAnalyticsMeasurementId}
            sharingPolicyDenied={sharingPolicyDenied}
          />
        ) : (
          <GoogleAnalytics
            googleAdsKey={googleAdsKey}
            pageLoadIdCookie={pageLoadIdCookie}
            measurementId={googleAnalyticsMeasurementId}
            sharingPolicyDenied={sharingPolicyDenied}
          />
        )}
        <div className="flex min-h-screen flex-col font-sans">
          <AppBarAboveTheList key={id} />
          {featureFlags?.enableChristmasHeader && (
            <div className="flex h-[50px] w-full items-center justify-center bg-[#2B4E4A] mobile:h-[35px]">
              <ChristmasEventLogo />
            </div>
          )}
          <CookieConsent
            showCookieConsentGdpr={showCookieConsentGdpr && !sharingPolicyDenied}
            setShowCookieConsentGdpr={setShowCookieConsentGdpr}
            showOnBottom={showCookieConsentOnBottom}
          />
          <main className="flex-1">
            <Outlet
              context={{
                displayCookieConsent: showCookieConsentGdpr,
                setShowCookieConsentGdpr,
                showCookieConsentOnBottom,
                clickId,
                updateClickId,
              }}
            />
          </main>
          <Footer />
          <ScrollToHash />
        </div>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}
